/**
 * Warm-start cache for the app's bootstrap state (profile, company,
 * location, subscription).
 *
 * On a returning launch the four contexts that gate first paint each make a
 * network round-trip before the UI is usable. This cache lets them render the
 * last-known value from disk INSTANTLY and revalidate over the network in the
 * background (stale-while-revalidate), so the loading gates only block on a
 * true cold start (nothing cached yet).
 *
 * Storage is @capacitor/preferences on native / localStorage on web, via
 * persistent-cache.ts — sandboxed and durable across app relaunches.
 *
 * SAFETY — every entry is keyed by a scope id:
 *  - profile / location  → the user's id (profiles.id / auth user id)
 *  - company / subscription → the company id
 * A different (or signed-out) user resolves a different key, so cross-user
 * reads are impossible: the worst case is a cache miss and the normal fetch.
 * Values are only ever READ after the live session is established for that same
 * scope, and every read is followed by a network revalidation that reconciles
 * (and, for the profile, re-runs the is_active / role checks). Nothing here is
 * a source of truth — it only decides whether to show a spinner or last-known
 * content for the one render before the network responds.
 */

import { persistentGet, persistentSet, persistentRemove } from './persistent-cache';

export type WarmKind = 'profile' | 'company' | 'location' | 'subscription';

/** `warm:<kind>:<scopeId>` — the scope id isolates users/companies. */
export function warmKey(kind: WarmKind, scopeId: string): string {
  return `warm:${kind}:${scopeId}`;
}

/** Read the last-known value for a scope. Resolves null on empty/missing scope. */
export function readWarm<T>(kind: WarmKind, scopeId: string | null | undefined): Promise<T | null> {
  if (!scopeId) return Promise.resolve(null);
  return persistentGet<T>(warmKey(kind, scopeId));
}

/**
 * Write-through the latest value. Fire-and-forget: a failed cache write must
 * never affect the live flow, so callers don't await this.
 */
export function writeWarm<T>(kind: WarmKind, scopeId: string | null | undefined, value: T): void {
  if (!scopeId) return;
  void persistentSet(warmKey(kind, scopeId), value);
}

/** Drop a scope's cached value (e.g. on forced sign-out / deactivation). */
export function clearWarm(kind: WarmKind, scopeId: string | null | undefined): void {
  if (!scopeId) return;
  void persistentRemove(warmKey(kind, scopeId));
}
